import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styles from './PaymentFailure.module.css';

function PaymentFailure() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [errorDetails, setErrorDetails] = useState(null);

  useEffect(() => {
    // Clear pending transaction data on failure
    localStorage.removeItem('pending_transaction_id');
    localStorage.removeItem('pending_plan_name');
    // Keep payment_context for now so we know to return to onboarding

    // Extract error details from URL parameters (sent by backend webhook)
    const urlParams = {
      transactionId: searchParams.get('txnid'),
      status: searchParams.get('status'), // 'failed' or 'cancelled'
      errorMessage: searchParams.get('error'),
      paymentType: searchParams.get('type'),
      amount: searchParams.get('amount')
    };

    console.log('Payment failure page loaded with params:', urlParams);
    setErrorDetails(urlParams);
  }, [searchParams]);

  const handleRetry = () => {
    // Check if this was an onboarding payment
    const paymentContext = localStorage.getItem('payment_context');
    
    // Clear payment context since we're retrying
    localStorage.removeItem('payment_context');
    
    if (paymentContext === 'onboarding') {
      console.log('PaymentFailure: Onboarding payment failed, returning to onboarding flow');
      navigate('/onboarding');
    } else {
      navigate('/subscriptions');
    }
  };

  const handleContactSupport = () => {
    const subject = errorDetails?.status === 'cancelled' 
      ? 'Payment Cancelled - Need Assistance' 
      : 'Payment Failed - Need Help';
    
    const body = `
Transaction ID: ${errorDetails?.transactionId || 'Not available'}
Status: ${errorDetails?.status || 'Unknown'}
Error: ${errorDetails?.errorMessage || 'No error message'}
Amount: ${errorDetails?.amount || 'Unknown'}
Payment Type: ${errorDetails?.paymentType || 'Unknown'}

Please help me resolve this payment issue.
    `.trim();

    window.open(`mailto:support@suspire.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
  };

  const getDisplayMessage = () => {
    if (!errorDetails) return 'Payment was cancelled or failed.';

    if (errorDetails.status === 'cancelled') {
      return 'Payment was cancelled by you. No amount has been charged.';
    } else if (errorDetails.status === 'failed') {
      return errorDetails.errorMessage || 'Payment failed due to a technical issue. No amount has been charged.';
    }
    
    return errorDetails.errorMessage || 'Payment was cancelled or failed.';
  };

  const getTitle = () => {
    if (errorDetails?.status === 'cancelled') {
      return 'Payment Cancelled';
    }
    return 'Payment Failed';
  };

  const getIcon = () => {
    if (errorDetails?.status === 'cancelled') {
      return '⚠'; // Warning icon for cancellation
    }
    return '✕'; // Error icon for failure
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={`${styles.icon} ${errorDetails?.status === 'cancelled' ? styles.warningIcon : styles.errorIcon}`}>
          {getIcon()}
        </div>
        <h1>{getTitle()}</h1>
        <p className={styles.message}>{getDisplayMessage()}</p>
        <p className={styles.subMessage}>Your subscription was not activated. No amount has been charged.</p>
        
        {errorDetails && (
          <div className={styles.details}>
            <h3>Transaction Details</h3>
            {errorDetails.transactionId && (
              <div className={styles.detailRow}>
                <span>Transaction ID:</span>
                <span>{errorDetails.transactionId}</span>
              </div>
            )}
            {errorDetails.amount && (
              <div className={styles.detailRow}>
                <span>Amount:</span>
                <span>${errorDetails.amount}</span>
              </div>
            )}
            <div className={styles.detailRow}>
              <span>Status:</span>
              <span className={styles.statusBadge}>{errorDetails.status || 'Unknown'}</span>
            </div>
            {errorDetails.paymentType && (
              <div className={styles.detailRow}>
                <span>Payment Type:</span>
                <span>{errorDetails.paymentType}</span>
              </div>
            )}
          </div>
        )}
        
        <div className={styles.actions}>
          <button 
            className={styles.primaryButton}
            onClick={handleRetry}
          >
            {errorDetails?.status === 'cancelled' ? 'Try Again' : 'Retry Payment'}
          </button>
          <button 
            className={styles.secondaryButton}
            onClick={handleContactSupport}
          >
            Contact Support
          </button>
        </div>
      </div>
    </div>
  );
}

export default PaymentFailure;
